import { Injectable } from '@angular/core'
import { BuilderService } from '../builder-services/builder.service'
import { stripeService } from '../pages/payment/stripe.service'
import { BlockDataService } from '../block-services/block-data.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { Observable } from 'rxjs'
import { HelpersService } from '../helpers.service'
import { LoginService } from '../login/login.service'
import { environment } from 'src/environments/environment'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { ToastService } from '../pages/toastr/toast.service'
import { Router } from '@angular/router'
import { WorksDialogComponent } from '../pages/works-dialog/works-dialog.component'
import { MatDialog } from '@angular/material/dialog'

@Injectable({
  providedIn: 'root'
})
export class SubscriptionFlowService {
  builderUrl!: string
  user_email!: string
  today: Date = new Date()
  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }
  isSubscriptionVisible: boolean = false
  public stripe: any = {}
  public package: any = {}
  constructor(
    private dialog: MatDialog,
    public builderService: BuilderService,
    public stripeService: stripeService,
    private blockDataService: BlockDataService,
    private settingsApi: SettingsApiService,
    private helpers: HelpersService,
    private loginService: LoginService,
    private http: HttpClient,
    private toastService: ToastService,
    private router: Router
  ) {
    this.subscriptionFlowHandler()
  }
  /**
   * Initializes the user's email and builder URL using helper services.
   */
  private initializeUserEmailAndBuilderUrl(): void {
    this.builderUrl = this.helpers.getBuilderUrl()
    this.user_email = this.loginService.getCurrentUser()
  }

  private subscriptionFlowHandler = () => {
    // Skip subscription flow entirely when running inside genie iframe (stacks-chat).
    // The parent app manages subscription state; showing popups here would block the UI.
    // if (this.builderService.isFromGenie()) {
    //   return
    // }
    // initialize user email and builder URL
    this.initializeUserEmailAndBuilderUrl()
    // Get customer data from Stripe
    this.fetchStripeData().subscribe((processedCustomerData: any) => {
      this.builderService.showBillingProgress = true
      // Extract the subscription package and continue the flow
      this.extractSubscriptionPackage().subscribe(
        () => {
          this.isSubscriptionVisible = true
          if (this.stripe?.subscriptions?.length !== 0) {
            // Check if the subscription is on hold
            this.checkOnholdStatus().subscribe((_subscriptionStatus: string) => {
              // Both onhold and cancelled are treated as free plan with product/order limits.
              // No popup, no auto-cancel, no redirect — just hide the progress indicator.
              this.builderService.showBillingProgress = false
            })
          } else {
            // No active subscriptions — free plan with limits.
            this.builderService.showBillingProgress = false
          }
        },
        (error) => {
          // extractSubscriptionPackage can fail when Stripe has no valid invoice data
          // (e.g., after subscription cancellation). Ensure progress is hidden gracefully.
          console.warn('Subscription package extraction failed:', error)
          this.builderService.showBillingProgress = false
        }
      )
    })
  }

  /**
   * Fetches Stripe data from the backend
   */
  public fetchStripeData() {
    return new Observable<any>((observer) => {
      this.blockDataService.getStripeData().subscribe((subId: any) => {
        this.stripeService.retrieveCustomerData(subId).subscribe((customerData: any) => {
          let processedCustomerData = this.processStripeResponse(customerData)
          observer.next(processedCustomerData)
        })
      })
    })
  }

  /**
   * Processes the response from Stripe, extracting the latest payment methods and invoices.
   * @param customerData The response object from Stripe API.
   */
  private processStripeResponse(customerData: any): void {
    this.stripe = {
      ...customerData,
      payment_methods: this.helpers.getLastItem(customerData?.payment_methods),
      invoices: customerData?.invoices[0]
    }
    this.builderService.invoices = customerData.invoices
    return this.stripe
  }

  /**
   * Checks if the subscription is onhold or not and return the status
   */
  checkOnholdStatus() {
    return new Observable<any>((observer) => {
      const nextPaymentDate = new Date((this.stripe?.upcoming_invoice?.next_payment_attempt || this.stripe?.invoices?.lines?.data?.[0]?.period?.end) * 1000)
      const daysLeft = Math.ceil((nextPaymentDate.getTime() - this.today.getTime()) / (1000 * 60 * 60 * 24))
      if (daysLeft <= 3 && daysLeft >= 0) {
        setTimeout(() => {
          this.daysLeftToast(daysLeft)
        }, 5000)
      }

      if (this.today.getTime() > nextPaymentDate.getTime()) {
        this.setProjectStatus('onhold').subscribe(() => {
          // this is an onhold subscription
          observer.next('onhold')
        })
      } else {
        // This is an active susbcription
        observer.next('active')
      }
    })
  }
  /**
   * Displays a toast notification when a subscription is about to expire.
   */
  daysLeftToast(daysLeft: any) {
    this.toastService.initiate({
      type: 'daysLeft',
      title: 'Reminder',
      content: `Your service will be updated ${daysLeft === 0 ? 'today' : `in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`}. Stay updated and enjoy uninterrupted access to all premium features!`,
      imgPath: '../assets/images/subscriptionFlow/toastOnholdImg.png',
      onClick: () => {
        this.router.navigate(['/billing'])
      }
    })
  }

  /**
   * Cancels the current subscription using the Stripe service.
   */
  cancelSubscription() {
    return new Observable<any>((observer) => {
      this.builderService.showUpdateProgress = true
      this.stripeService.cancelSubscription(this.stripe.invoices.subscription!).subscribe(
        (response: any) => {
          if (response) {
            this.builderService.showUpdateProgress = false
            // this.setProjectStatus('onhold')
            observer.next(response)
          } else {
            console.error('Error canceling subscription:', response)
            observer.error(response)
          }
        },
        (error) => {
          this.builderService.showUpdateProgress = false
          console.error('Error canceling subscription:', error)
          observer.error(error)
        }
      )
    })
  }

  /**
   * Updates the project's subscription status in the database if the status has changed.
   * @param status The new subscription status.
   */
  private setProjectStatus(status: string) {
    return new Observable<any>((observer) => {
      const subscriptionNewStatus = { status, date: this.today.toLocaleDateString() }
      this.settingsApi.getSettingsByType(String(this.builderService.selectedProject), 'subscription').then((subscriptionData: any) => {
        const currentStatus = subscriptionData?.['subscriptionStatus']
        if (!currentStatus || currentStatus !== status) {
          this.settingsApi.upsertSettingsByType(String(this.builderService.selectedProject), 'subscription', { subscriptionStatus: JSON.stringify([subscriptionNewStatus]) }).then(() => {
            observer.next(subscriptionNewStatus)
          })
        } else {
          observer.next('No changes in subscription status.')
        }
      })
    })
  }

// /**
//  * Checks if the last subscription status was "onhold" and handles expiration if it exceeds 30 days.
//  */
// private checkLastSubscriptionStatus() {
//   return new Observable<any>((observer) => {
//     this.db.getDatabase(`projects/${this.builderService.selectedProject}/subscription/subscriptionStatus`).subscribe((subscriptionStatuses: any) => {
//       const lastChange = this.helpers.getLastItem(subscriptionStatuses)
//       if (lastChange.status === 'onhold') {
//         const diffInDays = this.calculateDaysSince(lastChange.date)
//         observer.next(diffInDays)
//       }
//     })
//   })
// }

// /**
//  * Calculates the number of days since a given date.
//  * @param date The reference date in string format.
//  * @returns The difference in days.
//  */
// private calculateDaysSince(date: string): number {
//   const lastChangeDate = new Date(date)
//   return Math.floor((this.today.getTime() - lastChangeDate.getTime()) / (1000 * 60 * 60 * 24))
// }

// /**
//  * Handles expiration of "onhold" subscription status by canceling and deleting the project status.
//  */
// private handleExpiredOnHoldStatus(): void {
//   this.cancelSubscription().subscribe(() => {
//     this.setProjectStatus('deleted').subscribe(() => {})
//   })
// }

  /**
   * Retrieves all subscriptions .
   * @returns An Observable that emits an array of subscription objects.
   */
  getAllSubscriptions(): Observable<any> {
    return this.http.get(this.builderUrl + 'retrieve_all_subscribers', this.options)
  }
  /**
   * Retrieves the subscriber details for the given Stripe subscription ID.
   * @param stripe_subscription_id - The Stripe subscription ID .
   */
  getSubscriberDetails(stripe_subscription_id: string) {
    let postData = new URLSearchParams()
    postData.set('stripe_subscription_id', stripe_subscription_id)
    return this.http.post(this.builderUrl + 'retrieve_subscriber_by_id', postData.toString(), this.options)
  }
  /**
   * Retrieves a pricing package by its ID from the environment configuration.
   * @param id - The unique identifier of the pricing package to retrieve.
   * @returns The pricing package object if found, or `null` if not found.
   */
  getPricingPackageById(id: string): any {
    let pricingPackagesByRegion = [...environment.pricing_packages, ...environment.egypt_pricing_packages, ...environment.emirates_pricing_packages, ...environment.saudi_pricing_packages, ...environment.test_pricing_packages]
    const result = pricingPackagesByRegion.find((pkg) => pkg.pricing_id === id)
    if (result) {
      return result
    } else {
      return null
    }
  }

  /**
   * Opens a dialog to display the status of an active subscription.
   * The dialog width is set to 900px or 90% of the screen width, whichever is smaller.
   * After the dialog is closed, a message is logged to the console.
   */
  statusPopup = () => {
    const isCancelledStatus = this.builderService.cacheProjectReq?.response?.premium[0]?.status === 'deleted' || this.builderService.cacheProjectReq?.response?.premium[0]?.status === 'cancelled' || this.builderService.cacheProjectReq?.response?.details[0]?.status === 'cancelled'
    // When running inside genie iframe (stacks-chat), always allow closing the dialog
    // because the parent app handles subscription management
    const isNonClosable = isCancelledStatus && !this.builderService.isFromGenie()

    let dialogRef = this.dialog.open(WorksDialogComponent, {
      width: '900px',
      maxWidth: '100%',
      data: { name: 'subscriptionStatus' },
      disableClose: isNonClosable
    })

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed')
    })
  }
  /**
   * Cancels the auto-renewal of a Stripe subscription by switching it to manual invoicing.
   * @param stripe_subscription_id - The unique identifier of the Stripe subscription to cancel auto-renewal for.
   * @returns An observable representing the HTTP POST request to switch the subscription to manual invoicing.
   */
  cancelAutoRenewSubscription(stripe_subscription_id: string) {
    let postData = new URLSearchParams()
    postData.set('stripe_subscription_id', stripe_subscription_id)
    return this.http.post(this.builderUrl + 'switch_subscription_to_manual_invoicing', postData.toString(), this.options)
  }

  /**
   * Emits the description from the SubscriptionFlow object if available and completes immediately.
   * @returns {Observable<string>} An Observable that emits the description string and completes.
   */
  private getDescriptionObservable(): Observable<string> {
    return new Observable<string>((observer) => {
      const lines = this.stripe?.invoices?.lines?.data
      if (!lines || lines.length === 0) {
        observer.error('No invoice line items available.')
        return
      }
      const relevantLineItem = lines.find((lineItem: any) => !lineItem?.description?.startsWith('Unused time'))
      const description = relevantLineItem?.description

      if (description) {
        observer.next(description)
        observer.complete() // Complete immediately after emitting
      } else {
        observer.error('Description is not available.')
      }
    })
  }
  /**
   * Retrieves the subscription description asynchronously, parses it, and updates
   * the subscription package in the builder service.
   * @returns {Observable<void>} An Observable that completes when the extraction is done.
   */
  private extractSubscriptionPackage(): Observable<void> {
    return new Observable<void>((observer) => {
      this.builderService.showBillingProgress = false
      this.getDescriptionObservable().subscribe((description) => {
        if (description) {
          const parsedData = this.parseDescription(description)
          if (parsedData) {
            this.package = parsedData
            this.builderService.subscription_package = parsedData
            observer.next() // Emit completion
            observer.complete() // Mark as completed
          } else {
            console.error('Failed to parse description.')
            observer.error('Failed to parse description')
          }
        } else {
          console.error('Description is not available.')
          observer.error('Description is not available')
        }
      })
    })
  }

  /**
   * Parses a subscription description string and extracts structured data.
   * @param {string} description - The subscription description to parse.
   * @returns {object | null} An object containing parsed data (quantity, name, price, period),
   * or null if the description format is invalid.
   */
  private parseDescription(description: string): any {
    const regex = /^(\d+)\s+×\s+(.+?)\s+\(at\s+([^\s]+)\s+\/\s+(.+)\)$/
    const matches = description.match(regex)

    if (matches) {
      const quantity = Number(matches[1])
      const name = matches[2]
      const price = matches[3]
      const period = matches[4]

      // Extracting the type from the name
      let type: string | null = null
      if (name.includes('growth')) {
        type = 'Growth'
      } else if (name.includes('Starter')) {
        type = 'Starter'
      } else {
        type = '' // Default type if no specific keyword is found
      }
      return { quantity, name, price, period, type }
    } else {
      console.error('Invalid description format:', description)
      return null
    }
  }
}
